'use client'

import {useCallback, useRef, type ReactNode} from 'react'

type AccordionKeyboardNavProps = {
  children: ReactNode
  className?: string
  'aria-label'?: string
}

/**
 * Wrapper that adds keyboard navigation (arrow up/down/home/end)
 * between <summary> elements inside <details> accordion items.
 */
export function AccordionKeyboardNav({
  children,
  className,
  'aria-label': ariaLabel,
}: AccordionKeyboardNavProps) {
  const ref = useRef<HTMLDivElement>(null)

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    const target = e.target as HTMLElement
    if (target.tagName !== 'SUMMARY') return

    const container = ref.current
    if (!container) return

    const summaries = Array.from(container.querySelectorAll('summary'))
    const idx = summaries.indexOf(target)
    if (idx === -1) return

    let next: HTMLElement | undefined

    if (e.key === 'ArrowDown' && idx < summaries.length - 1) {
      next = summaries[idx + 1]
    } else if (e.key === 'ArrowUp' && idx > 0) {
      next = summaries[idx - 1]
    } else if (e.key === 'Home') {
      next = summaries[0]
    } else if (e.key === 'End') {
      next = summaries[summaries.length - 1]
    }

    if (next) {
      e.preventDefault()
      next.focus()
    }
  }, [])

  return (
    <div ref={ref} role="region" aria-label={ariaLabel} className={className} onKeyDown={onKeyDown}>
      {children}
    </div>
  )
}
